import React, { useState } from "react";
import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { Check, ChevronDown, Wand2 } from "lucide-react";

// Mirrors Ai::CvParserClient::MODELS and ENGINES in the parser's app/extractor.py — all
// three lists have to agree or the parser answers 422. Nothing is persisted: the choice rides with
// the request and comes back on the result as `model_version`.
export const PARSER_MODELS = [
  { value: "luna", label: "GPT 5.6 Luna", hint: "Faster and cheaper. Default." },
  { value: "mistral-large", label: "Mistral Large 3.0", hint: "Slower, but GDPR friendly." },
];

export const DEFAULT_PARSER_MODEL = "luna";

export const parserModelLabel = (value) =>
  PARSER_MODELS.find((m) => m.value === value)?.label || PARSER_MODELS[0].label;

const ModelList = ({ value, onPick, onClose }) =>
  PARSER_MODELS.map((model) => (
    <MenuItem
      key={model.value}
      selected={model.value === value}
      onClick={() => {
        onPick(model.value);
        if (onClose) onClose();
      }}
      sx={{ alignItems: "flex-start", gap: 1, py: 0.75 }}
    >
      <Box sx={{ width: 16, pt: 0.25, flexShrink: 0 }}>
        {model.value === value && <Check size={14} />}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
          {model.label}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "hsl(150 10% 45%)" }}>{model.hint}</Typography>
      </Box>
    </MenuItem>
  ));

// The row action: one click extracts with the current model, the caret picks a different one.
// Keeps the default a single click and costs no width until it is opened.
export const ParserModelSplitButton = ({
  value,
  onChange,
  onRun,
  label = "Extract",
  icon = <Wand2 size={13} />,
  disabled = false,
}) => {
  const [anchor, setAnchor] = useState(null);
  const stop = (fn) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    fn(e);
  };

  const base = {
    textTransform: "none",
    fontSize: 12,
    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
    height: 28,
    minHeight: 0,
    minWidth: 0,
    color: "hsl(80 97% 72%)",
    backgroundColor: "hsl(150 38% 17%)",
    "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
  };

  return (
    <>
      <Box sx={{ display: "inline-flex" }}>
        <Button
          size="small"
          disableRipple
          disabled={disabled}
          startIcon={icon}
          onClick={stop(() => onRun(value))}
          sx={{
            ...base,
            borderRadius: "6px 0 0 6px",
            px: 1,
            "& .MuiButton-startIcon": { mr: 0.5 },
          }}
        >
          {label}
        </Button>
        <Button
          size="small"
          disableRipple
          disabled={disabled}
          aria-label={`Parser: ${parserModelLabel(value)}. Choose another`}
          onClick={stop((e) => setAnchor(e.currentTarget))}
          sx={{
            ...base,
            borderRadius: "0 6px 6px 0",
            px: 0.5,
            ml: "1px",
          }}
        >
          <ChevronDown size={13} />
        </Button>
      </Box>

      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={stop(() => setAnchor(null))}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <ModelList value={value} onPick={onChange} onClose={() => setAnchor(null)} />
      </Menu>
    </>
  );
};

// The form/modal variant: a labelled row of choices rather than a hidden menu, for the places
// where the recruiter is already reading options rather than acting on a list.
export const ParserModelPicker = ({ value, onChange, disabled = false }) => {
  const [anchor, setAnchor] = useState(null);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: 12, color: "hsl(150 10% 45%)" }}>Parse with</Typography>
      <Button
        size="small"
        disableRipple
        disabled={disabled}
        endIcon={<ChevronDown size={13} />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          textTransform: "none",
          fontSize: 12,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          borderRadius: "6px",
          px: 1,
          color: "hsl(150 38% 17%)",
          border: "1px solid hsl(150 10% 85%)",
        }}
      >
        {parserModelLabel(value)}
      </Button>
      <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}>
        <ModelList value={value} onPick={onChange} onClose={() => setAnchor(null)} />
      </Menu>
    </Box>
  );
};
